import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { ProductOverride } from 'src/app/models/product-override';
import { ProductReference } from 'src/app/models/layout/product-reference';
import { ProductShelfPosition } from 'src/app/models/layout/product-shelf-position';
import { PlanogramProduct } from 'src/app/models/layout/planogram-product';
import {
  InspectedProductLocation,
  ProductInspectorService,
} from 'src/app/services/product-inspector.service';
import { ModalService } from 'src/app/services/modal.service';
import { OverlayService, OverlayType } from 'src/app/services/overlay.service';
import { ProductOverrideService } from 'src/app/services/product-override.service';
import { OverrideProductModalComponent } from 'src/app/modals/override-product-modal/override-product-modal.component';
import { ZoomService } from 'src/app/services/zoom.service';
import { ContextMenuService } from 'src/app/services/contextmenu.service';
import { LayoutService } from 'src/app/services/layout.service';
import { BehaviorSubject, combineLatest, Subscription } from 'rxjs';
import { LabelService } from 'src/app/services/label.service';
import { VisualAnalysisService } from 'src/app/services/visualanalysis.service';
import { CapabilitiesService } from 'src/app/services/capabilities.service';
import { CapabilityType } from 'src/app/models/capability-type';
import { DragContextService } from 'src/app/services/dragcontext.service';
import { AsyncPipe } from '@angular/common';
import { ImageComponent } from '../../../../image/image.component';
import { CreateRangePipe } from '../../../../pipes/create-range.pipe';

@Component({
    selector: 'app-planogram-manual-product',
    templateUrl: './planogram-manual-product.component.html',
    styleUrls: ['./planogram-manual-product.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ImageComponent, AsyncPipe, CreateRangePipe]
})
export class PlanogramManualProductComponent implements OnInit, OnDestroy {
  @Input() product: PlanogramProduct | null = null;
  @Input() dropIndex: number = -1;
  @Input() shelfIndex: number = -1;
  @Input() stackIndex: number = -1;
  @Input() productIndex: number = -1;
  @Output() pasteProductBefore = new EventEmitter<{ productIndex: number }>();
  @Output() pasteProductAfter = new EventEmitter<{ productIndex: number }>();
  @Output() addHFacing = new EventEmitter<{ productIndex: number }>();
  @Output() removeHFacing = new EventEmitter<{ productIndex: number }>();
  @Output() addVFacing = new EventEmitter<{ productIndex: number }>();
  @Output() removeVFacing = new EventEmitter<{ productIndex: number }>();

  inspectedProductId: number | null = null;
  inspectedProductLocation: InspectedProductLocation | null = null;
  labelStyle$ = new BehaviorSubject<any>({});
  highlightedOverlayIndex: number | null = null;

  // Latest values from the label/overlay subscriptions. These are cached because
  // they arrive via service emissions rather than @Input, but everything that is
  // PRODUCT-KEYED (label, overlay colour, shelf position, image dimensions) is
  // derived from them by GETTERS below — read at render time against the current
  // `product`. This component is OnPush and its facings render via track-by-index,
  // so on a facing add/remove (or undo/redo) Angular reuses the instance and
  // rebinds [product]. When immer preserves the product object reference, Angular's
  // Object.is input check sees no change and skips ngOnChanges — so caching derived
  // state into fields (set in subscriptions / ngOnChanges) goes stale and a reused
  // facing shows its previous neighbour's value. The image never had this problem
  // because it binds `product.productId` live; deriving via getters gives the same
  // guarantee to the label, overlay and style.
  private latestLabelMap: Map<number, string | null> = new Map();
  private latestOverlayType: OverlayType = OverlayType.NONE;
  private latestLegendMap: Map<number, number> = new Map();

  private subscriptions: Subscription = new Subscription();

  constructor(
    private zoomService: ZoomService,
    private layoutService: LayoutService,
    private labelService: LabelService,
    private overlayService: OverlayService,
    private visualAnalysisService: VisualAnalysisService,
    private contextMenuService: ContextMenuService,
    private productInspectorService: ProductInspectorService,
    private modalService: ModalService,
    private productOverrideService: ProductOverrideService,
    private capabilitiesService: CapabilitiesService,
    private elementRef: ElementRef,
    private dragContextService: DragContextService,
    private cdr: ChangeDetectorRef
  ) {}

  // --- Product-keyed derived state (render-time getters) -------------------
  // All of these dereference the CURRENT `this.product` at render time, the same
  // way the template binds the image (`[productId]="product?.productId"`), so they
  // can never drift from the rendered product regardless of immer reference reuse,
  // track-by-index recycling, undo/redo, or service-emission ordering.

  /** ProductReference for the current product, resolved live from planogramItems
   *  (keyed by productId, stable until products are imported/refreshed). */
  private get currentProductRef(): ProductReference | null {
    return this.product
      ? this.layoutService.planogramItems.get(this.product.productId) ?? null
      : null;
  }

  /** DOS / barcode / brand / etc. label for the current product. */
  get label(): string | null {
    return this.product
      ? this.latestLabelMap.get(this.product.productId) ?? null
      : null;
  }

  get shelfPosition(): string {
    const ref = this.currentProductRef;
    return ref ? ProductShelfPosition[ref.shelfPosition] : '';
  }

  /** Legend colour index for the current product under the active overlay; null
   *  for NONE / VISUAL_ANALYSIS (which don't use the legend). Feeds `inspecting`. */
  get currentProductOverlayIndex(): number | null {
    if (
      !this.product ||
      this.latestOverlayType === OverlayType.NONE ||
      this.latestOverlayType === OverlayType.VISUAL_ANALYSIS
    ) {
      return null;
    }
    return this.latestLegendMap.get(this.product.productId) ?? null;
  }

  /** Overlay background-colour style applied to the product wrapper and image. */
  get overlayStyle(): { [key: string]: string } {
    if (!this.product || this.latestOverlayType === OverlayType.NONE) return {};

    if (this.latestOverlayType === OverlayType.VISUAL_ANALYSIS) {
      const customColor = this.visualAnalysisService
        .getCustomColors()
        .get(this.product.productId);
      return customColor ? { 'background-color': customColor } : {};
    }

    const colorIndex = this.latestLegendMap.get(this.product.productId);
    return typeof colorIndex !== 'undefined'
      ? { 'background-color': this.overlayService.overlayColors[colorIndex] }
      : {};
  }

  /** Zoom-scaled image dimensions merged with the overlay colour. */
  get productStyle(): { [key: string]: string } {
    const ref = this.currentProductRef;
    if (!this.product || !this.product.dimensions || !ref) {
      return { ...this.overlayStyle };
    }

    const zoom = this.zoomService.getCurrentZoom();
    const maxHeight = zoom * this.product.dimensions.height;
    const minHeight =
      zoom *
      (this.product.dimensions.height -
        this.product.dimensions.height * ref.squeezeH);
    const maxWidth = zoom * this.product.dimensions.width;
    const minWidth =
      zoom *
      (this.product.dimensions.width -
        this.product.dimensions.width * ref.squeezeW);

    return {
      maxWidth: `${maxWidth}px`,
      minWidth: `${minWidth}px`,
      maxHeight: `${maxHeight}px`,
      minHeight: `${minHeight}px`,
      ...this.overlayStyle,
    };
  }

  ngOnInit(): void {
    // Product-keyed state is derived by the getters above; the subscriptions only
    // cache the latest service emissions and markForCheck so the OnPush view
    // re-renders (and re-reads the getters) when those inputs change.

    // planogramItems entries only change on product import/refresh; re-check then
    // so the live-resolving getters pick up the refreshed references.
    this.subscriptions.add(
      this.layoutService.productsChanged$.subscribe(() => {
        this.cdr.markForCheck();
      })
    );

    this.subscriptions.add(
      this.labelService.labels$.subscribe((labelMap) => {
        this.latestLabelMap = labelMap;
        this.cdr.markForCheck();
      })
    );

    this.subscriptions.add(
      combineLatest([
        this.overlayService.overlayType$,
        this.overlayService.productLegendMap$,
        this.zoomService.zoom$,
      ]).subscribe(([overlayType, legendMap, zoom]) => {
        this.latestOverlayType = overlayType;
        this.latestLegendMap = legendMap;
        this.updateLabelStyle();
        this.cdr.markForCheck();
      })
    );

    this.subscriptions.add(
      this.overlayService.highlightedOverlayIndex$.subscribe(index => {
        this.highlightedOverlayIndex = index;
        this.cdr.markForCheck();
      })
    );

    this.subscriptions.add(
      this.productInspectorService.inspectedProduct$.subscribe((productId) => {
        this.inspectedProductId = productId;
        if (this.inspecting)
          this.productInspectorService.requestScrollIntoView(
            this.elementRef.nativeElement
          );
        this.cdr.markForCheck();
      })
    );

    this.subscriptions.add(
      this.productInspectorService.inspectedProductLocation$.subscribe(
        (productLocation) => {
          this.inspectedProductLocation = productLocation;
          if (this.inspecting)
            this.productInspectorService.requestScrollIntoView(
              this.elementRef.nativeElement
            );
          this.cdr.markForCheck();
        }
      )
    );
  }

  ngOnDestroy() {
    // Clean up all subscriptions
    this.subscriptions.unsubscribe();

    // Clean up BehaviorSubject
    if (this.labelStyle$) this.labelStyle$.complete();
  }

  get inspecting(): boolean {
    // Priority 1: Check if this product belongs to the highlighted overlay legend item
    if (this.highlightedOverlayIndex !== null && this.currentProductOverlayIndex === this.highlightedOverlayIndex) {
      return true;
    }

    // Priority 2: Standard inspection
    if (
      this.inspectedProductLocation == null &&
      this.product?.productId === this.inspectedProductId
    ) {
      return true;
    } else if (
      this.inspectedProductLocation?.dropIndex === this.dropIndex &&
      this.inspectedProductLocation.shelfIndex === this.shelfIndex &&
      this.inspectedProductLocation.stackIndex === this.stackIndex &&
      this.inspectedProductLocation.productIndex === this.productIndex
    ) {
      return true;
    }
    return false;
  }

  inspectProduct(event: MouseEvent) {
    if (this.product != null) {
      if (
        event.ctrlKey &&
        this.capabilitiesService.hasCapability(
          CapabilityType.GENERATE_PLANOGRAM
        )
      ) {
        this.modalService
          .openModalWithOptions(
            OverrideProductModalComponent,
            {
              centered: true,
            },
            {
              data: this.product.productId,
            }
          )
          .then(async (override: ProductOverride | null) => {
            if (override != null) {
              this.productOverrideService.setOverride(
                this.product!.productId,
                override
              );
            }
          });
      } else {
        this.productInspectorService.inspect(this.product.productId, {
          dropIndex: this.dropIndex,
          shelfIndex: this.shelfIndex,
          stackIndex: this.stackIndex,
          productIndex: this.productIndex,
        });
      }
    }
  }

  onProductRightClick(event: MouseEvent): void {
    event.stopImmediatePropagation();
    event.preventDefault();
    this.contextMenuService.openProductContextMenu(
      event,
      this.dropIndex,
      this.shelfIndex,
      this.stackIndex,
      this.productIndex
    );
  }

  onMouseDown(event: MouseEvent) {
    const productRef = this.currentProductRef;
    if (event.button !== 0 || !productRef || !this.product) return;

    {{ // Prevent dragging if user can't edit
    if (!this.capabilitiesService.hasCapability(CapabilityType.EDIT_PLANOGRAM)) return; }}

    event.preventDefault();
    event.stopPropagation();

    this.dragContextService.onMouseDownFromStack(
      this.dropIndex,
      this.shelfIndex,
      this.stackIndex,
      this.productIndex,
      productRef,
      this.product,
      event
    );
  }

  private updateLabelStyle() {
    let maxFontSize = 15;
    let minFontSize = 9;

    // Map zoom (1-8) to font size (6-12)
    let fontSize =
      minFontSize +
      ((this.zoomService.getCurrentZoom() - 1) / 7) *
        (maxFontSize - minFontSize);

    this.labelStyle$.next({
      'font-size': `${fontSize}px`,
    });
  }
}
